import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig';
import Swal from 'sweetalert2';

function AccountForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(id ? true : false);
    const [groups, setGroups] = useState([]);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [isVerifyingGst, setIsVerifyingGst] = useState(false);

    const [formData, setFormData] = useState({
        accountGST: '',
        accountName: '',
        accountGroup: '',
        accountMobileNumber: '',
        accountEmail: '',
        accountCountry: '',
        accountState: '',
        accountCity: '',
        accountPinCode: '',
        accountStateCode: '',
        accountPANNo: '',
        accountType: '',
        bankName: '',
        branchName: '',
        ifscCode: '',
        bankAccountNumber: '',
        bankOpeningBalance: '',
        accountAddress: '',
        accountBankAddress: ''
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const token = sessionStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };

                // Fetch account groups for dropdown
                const groupRes = await axios.get(`${API_BASE_URL}/account-group`, { headers });
                if (Array.isArray(groupRes.data)) setGroups(groupRes.data);

                // Fetch Countries
                const countryRes = await axios.get(`${API_BASE_URL}/country`, { headers });
                setCountries(countryRes.data);

                if (id) {
                    const res = await axios.get(`${API_BASE_URL}/account/${id}`, { headers });
                    if (res.data) setFormData(res.data);
                }
            } catch (err) {
                console.error("Error loading account data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [id]);

    useEffect(() => {
        const fetchStates = async () => {
            if (!formData.accountCountry) {
                setStates([]);
                return;
            }
            try {
                const token = sessionStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };
                // Find country ID - support both name and ID depending on how it's stored
                const selectedCountry = countries.find(c => (c.country_name || c.countryName) === formData.accountCountry);
                if (selectedCountry) {
                    const countryId = selectedCountry.id || selectedCountry.countryId;
                    const res = await axios.get(`${API_BASE_URL}/state?countryId=${countryId}`, { headers });
                    setStates(res.data);
                }
            } catch (err) {
                console.error("Error fetching states:", err);
            }
        };
        fetchStates();
    }, [formData.accountCountry, countries]);

    useEffect(() => {
        const fetchCities = async () => {
            if (!formData.accountState) {
                setCities([]);
                return;
            }
            try {
                const token = sessionStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };
                const selectedState = states.find(s => (s.state_name || s.stateName) === formData.accountState);
                if (selectedState) {
                    const stateId = selectedState.id || selectedState.stateId;
                    const res = await axios.get(`${API_BASE_URL}/city?stateId=${stateId}`, { headers });
                    setCities(res.data);
                }
            } catch (err) {
                console.error("Error fetching cities:", err);
            }
        };
        fetchCities();
    }, [formData.accountState, states]);

    const handleGSTVerify = async (gstToVerify) => {
        const gst = (gstToVerify || '').trim().toUpperCase();
        if (!gst) {
            toast.warn('Please enter a GST number');
            return;
        }
        if (gst.length !== 15) {
            toast.warn('GST number must be exactly 15 characters');
            return;
        }

        setIsVerifyingGst(true);
        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/gst/verify/${gst}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();
            if (res.ok && data.success && data.data) {
                const info = data.data;
                toast.success(`GST verified: ${info.legalName || info.tradeName}`);

                // Extract State Code from GSTIN (first 2 digits)
                const stateCode = gst.substring(0, 2);

                setFormData(prev => ({
                    ...prev,
                    accountGST: info.gstNumber || prev.accountGST,
                    accountName: info.tradeName || info.legalName || prev.accountName,
                    accountAddress: info.address || prev.accountAddress,
                    accountState: info.state || prev.accountState,
                    accountCity: info.city || prev.accountCity,
                    accountPinCode: info.pincode || prev.accountPinCode,
                    accountPANNo: info.panNumber || prev.accountPANNo,
                    accountStateCode: stateCode || prev.accountStateCode,
                    accountCountry: 'India'
                }));
            } else {
                toast.error(data.message || 'GST verification failed');
                // Clear fields on failure
                setFormData(prev => ({
                    ...prev,
                    accountName: '',
                    accountAddress: '',
                    accountState: '',
                    accountCity: '',
                    accountPinCode: '',
                    accountPANNo: '',
                    accountStateCode: '',
                    accountCountry: ''
                }));
            }
        } catch (err) {
            console.error('GST verify error:', err);
            toast.error('Error verifying GST details');
            // Clear fields on error
            setFormData(prev => ({
                ...prev,
                accountName: '',
                accountAddress: '',
                accountState: '',
                accountCity: '',
                accountPinCode: '',
                accountPANNo: '',
                accountStateCode: '',
                accountCountry: ''
            }));
        } finally {
            setIsVerifyingGst(false);
        }
    };

    const handleChange = (e) => {
                let { name, value } = e.target;
        if (name && name.toLowerCase().includes('gst')) {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (value.length > 15) value = value.substring(0, 15);
        }
        if (name.toLowerCase().includes('adhar') || name.toLowerCase().includes('aadhar')) {
            let val = value.replace(/\D/g, '');
            if (val.length > 12) val = val.substring(0, 12);
            let formatted = val;
            if (val.length > 4) formatted = val.substring(0, 4) + ' ' + val.substring(4);
            if (val.length > 8) formatted = formatted.substring(0, 9) + ' ' + val.substring(8);
            setFormData(prev => ({ ...prev, [name]: formatted }));
            return;
        }
        if (
            name.toLowerCase().includes('pincode') || 
            name.toLowerCase().includes('pin_code') || 
            name.toLowerCase().includes('accountnumber') || 
            name.toLowerCase().includes('account_number') ||
            name.toLowerCase().includes('accountno') || 
            name.toLowerCase().includes('account_no')
        ) {
            const onlyDigits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            return;
        }
        
        if (name.toLowerCase().includes('vehicle')) {
            setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
            return;
        }
        if (name.toLowerCase().includes('mobile')) {
            const onlyDigits = value.replace(/\D/g, '');
            if (onlyDigits.length <= 10) {
                setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            }
            return;
        }
        if (name === 'accountGST') {
            const upperVal = value.toUpperCase();
            setFormData(prev => ({
                ...prev,
                [name]: upperVal,
                accountName: '',
                accountAddress: '',
                accountState: '',
                accountCity: '',
                accountPinCode: '',
                accountPANNo: '',
                accountStateCode: '',
                accountCountry: ''
            }));
            if (upperVal.length === 15) {
                handleGSTVerify(upperVal);
            }
        } else if (name === 'accountGroup') {
            const selectedGroup = groups.find(g => String(g._id) === String(value));
            const groupName = selectedGroup ? (selectedGroup.accountGroupName || '').toLowerCase() : '';
            const isBankGroup = groupName.includes('bank');

            setFormData(prev => ({ 
                ...prev, 
                [name]: value,
                accountType: isBankGroup ? 'Bank Account' : prev.accountType
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        e.preventDefault();
        if (!formData.accountGroup) {
            toast.warn("Please select an Account Group");
            return;
        }
        try {
            const token = sessionStorage.getItem('token');
            const url = id ? `${API_BASE_URL}/account/${id}` : `${API_BASE_URL}/account`;
            const method = id ? 'put' : 'post';

            await axios[method](url, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(`Account ${id ? 'updated' : 'added'} successfully!`);
            navigate(-1);
        } catch (err) {
            console.error("Error saving account:", err);
            toast.error("Failed to save account");
        }
    };

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="voucher-entry-container">
            <h1 className="voucher-entry-title" style={{ textAlign: 'left', borderBottom: '2px solid #000', paddingBottom: '10px', width: 'fit-content' }}>Add Account</h1>

            <form onSubmit={handleSubmit} className="voucher-entry-form" style={{ marginTop: '30px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '15px', marginBottom: '20px' }}>
                    <div className="ve-field" style={{ display: 'flex', flexDirection: 'column' }}>
                        <label>Account GST</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                name="accountGST"
                                placeholder="ENTER ACCOUNT GST"
                                value={formData.accountGST}
                                onChange={handleChange}
                                style={{ flex: 1 }}
                            />
                            <button
                                type="button"
                                onClick={() => handleGSTVerify(formData.accountGST)}
                                disabled={isVerifyingGst}
                                style={{
                                    padding: '0 12px',
                                    background: '#0b3a54',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    transition: 'background 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#0d4a68'}
                                onMouseOut={(e) => e.target.style.background = '#0b3a54'}
                            >
                                {isVerifyingGst ? 'Verifying...' : 'Verify'}
                            </button>
                        </div>
                    </div>
                    <div className="ve-field">
                        <label>Account Name</label>
                        <input name="accountName" placeholder="Enter Account Trade Name" value={formData.accountName} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Account Group</label>
                        <select name="accountGroup" value={formData.accountGroup} onChange={handleChange}>
                            <option value="">Select Purpose</option>
                            {groups.map(g => (
                                <option key={g._id} value={g._id}>{g.accountGroupName}</option>
                            ))}
                        </select>
                    </div>
                    <div className="ve-field">
                        <label>Account Mobile Number</label>
                        <input name="accountMobileNumber" placeholder="Enter Mobile Number" value={formData.accountMobileNumber} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Account Email</label>
                        <input name="accountEmail" placeholder="Enter Email" value={formData.accountEmail} onChange={handleChange} />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginBottom: '20px' }}>
                    <div className="ve-field">
                        <label>Account Country</label>
                        <select name="accountCountry" value={formData.accountCountry} onChange={handleChange}>
                            <option value="">Select Country</option>
                            {countries.map(c => (
                                <option key={c.id || c.countryId} value={c.country_name || c.countryName}>
                                    {c.country_name || c.countryName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="ve-field">
                        <label>Account State</label>
                        <select name="accountState" value={formData.accountState} onChange={handleChange}>
                            <option value="">Select State</option>
                            {states.map(s => (
                                <option key={s.id || s.stateId} value={s.state_name || s.stateName}>
                                    {s.state_name || s.stateName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="ve-field">
                        <label>Account City</label>
                        <select name="accountCity" value={formData.accountCity} onChange={handleChange}>
                            <option value="">Select City</option>
                            {cities.map(c => (
                                <option key={c.id || c.cityId} value={c.city_name || c.cityName}>
                                    {c.city_name || c.cityName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="ve-field">
                        <label>Account Pin Code</label>
                        <input name="accountPinCode" placeholder="Enter Account Pin Code" value={formData.accountPinCode} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Account State Code</label>
                        <input name="accountStateCode" placeholder="Enter Account State Code" value={formData.accountStateCode} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Account PAN No</label>
                        <input name="accountPANNo" placeholder="Enter Account PAN No" value={formData.accountPANNo} onChange={handleChange} />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginBottom: '20px' }}>
                    <div className="ve-field">
                        <label>Account Type</label>
                        <input name="accountType" placeholder="Enter Account Type" value={formData.accountType} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Bank Name</label>
                        <input name="bankName" placeholder="Enter Bank Name" value={formData.bankName} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Branch Name</label>
                        <input name="branchName" placeholder="Enter Branch Name" value={formData.branchName} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>IFSC Code</label>
                        <input name="ifscCode" placeholder="Enter IFSC Code" value={formData.ifscCode} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Bank Account Number</label>
                        <input name="bankAccountNumber" placeholder="Enter Bank Account Number" value={formData.bankAccountNumber} onChange={handleChange} />
                    </div>
                    <div className="ve-field">
                        <label>Bank Opening Balance</label>
                        <input name="bankOpeningBalance" placeholder="Enter Bank Opening_Balance" value={formData.bankOpeningBalance} onChange={handleChange} />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                    <div className="ve-field">
                        <label>Account Address</label>
                        <textarea
                            name="accountAddress"
                            placeholder="Enter Account Address"
                            value={formData.accountAddress}
                            onChange={handleChange}
                            style={{ height: '120px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px', outline: 'none' }}
                        />
                    </div>
                    <div className="ve-field">
                        <label>Account Bank Address</label>
                        <textarea
                            name="accountBankAddress"
                            placeholder="Enter Account Bank Address"
                            value={formData.accountBankAddress}
                            onChange={handleChange}
                            style={{ height: '120px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px', outline: 'none' }}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '15px' }}>
                    <button type="submit" className="ve-submit-btn" style={{ width: '120px' }}>Submit</button>
                    <button type="button" onClick={() => navigate(-1)} className="ve-remove-btn" style={{ width: '120px', background: '#848a96', marginTop: '0' }}>Cancle</button>
                </div>
            </form>
        </div>
    );
}

export default AccountForm;
